// src/pages/BlogPost.js

import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, Clock, ChevronRight, Share2, 
  Check, ArrowRight, Sparkles, HelpCircle, ChevronDown, 
  ChevronUp, Phone, Award, BookOpen
} from 'lucide-react';
import { BLOG_POSTS } from '../data/blogData';
import '../styles/Blog.scss';

export default function BlogPost() {
  const { slug } = useParams();
  const [activeFaq, setActiveFaq] = useState(null);
  const [copied, setCopied] = useState(false);

  const post = BLOG_POSTS.find(p => p.slug === slug || (p.aliases && p.aliases.includes(slug)));

  if (!post) {
    return <Navigate to="/blogs" replace />;
  }

  const toggleFaq = (idx) => {
    setActiveFaq(activeFaq === idx ? null : idx);
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // 3 Related Posts (excluding current)
  const relatedPosts = BLOG_POSTS.filter(p => p.id !== post.id).slice(0, 3);

  // Structured Data Schema (BlogPosting + BreadcrumbList + FAQPage)
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `https://prabhattaravidyaniketan.in/blog/${post.slug}#article`,
        "isPartOf": {
          "@type": "WebPage",
          "@id": `https://prabhattaravidyaniketan.in/blog/${post.slug}`
        },
        "headline": post.title,
        "description": post.metaDescription,
        "image": `https://prabhattaravidyaniketan.in${post.coverImage}`,
        "datePublished": `${post.publishDate}T08:00:00+05:30`,
        "dateModified": `${post.publishDate}T10:00:00+05:30`,
        "mainEntityOfPage": `https://prabhattaravidyaniketan.in/blog/${post.slug}`,
        "author": {
          "@type": "Organization",
          "name": "Prabhat Tara Vidya Niketan",
          "url": "https://prabhattaravidyaniketan.in"
        },
        "publisher": {
          "@type": "EducationalOrganization",
          "name": "Prabhat Tara Vidya Niketan",
          "url": "https://prabhattaravidyaniketan.in",
          "logo": {
            "@type": "ImageObject",
            "url": "https://prabhattaravidyaniketan.in/images/logo.jpg"
          }
        },
        "keywords": [post.primaryKeyword, ...post.secondaryKeywords].join(', ')
      },
      {
        "@type": "BreadcrumbList",
        "@id": `https://prabhattaravidyaniketan.in/blog/${post.slug}#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://prabhattaravidyaniketan.in/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blogs & Guides",
            "item": "https://prabhattaravidyaniketan.in/blogs"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": post.title,
            "item": `https://prabhattaravidyaniketan.in/blog/${post.slug}`
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": `https://prabhattaravidyaniketan.in/blog/${post.slug}#faq`,
        "mainEntity": post.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      }
    ]
  };

  return (
    <div className="blog-post-page">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={[post.primaryKeyword, ...post.secondaryKeywords].join(', ')} />
        <link rel="canonical" href={`https://prabhattaravidyaniketan.in/blog/${post.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={`https://prabhattaravidyaniketan.in/blog/${post.slug}`} />
        <meta property="og:image" content={`https://prabhattaravidyaniketan.in${post.coverImage}`} />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:section" content={post.category} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={`https://prabhattaravidyaniketan.in${post.coverImage}`} />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="post-breadcrumbs" aria-label="Breadcrumb">
        <div className="breadcrumbs-inner">
          <Link to="/">Home</Link>
          <ChevronRight size={13} />
          <Link to="/blogs">Blogs & Guides</Link>
          <ChevronRight size={13} />
          <span className="current-title">{post.title}</span>
        </div>
      </nav>

      {/* Article Header */}
      <header className="article-header">
        <div className="header-inner">
          <span className="article-meta-pill">
            <Sparkles size={13} /> {post.category}
          </span>
          <h1>{post.title}</h1>

          <div className="author-info-bar">
            <div className="author-box">
              <img src={post.author.avatar} alt={post.author.name} />
              <div className="author-text">
                <span className="name">{post.author.name}</span>
                <span className="role">{post.author.role}</span>
              </div>
            </div>

            <div className="post-stats">
              <span><Calendar size={13} /> {post.publishDate}</span>
              <span>•</span>
              <span><Clock size={13} /> {post.readTime}</span>
              <span>•</span>
              <button 
                onClick={handleShare} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0c278c', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600, fontSize: '0.85rem' }}
                title="Share Article Link"
              >
                {copied ? <Check size={14} color="#27ae60" /> : <Share2 size={14} />}
                <span>{copied ? 'Link Copied!' : 'Share'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Cover Image */}
      <div className="article-cover-wrap">
        <img src={post.coverImage} alt={post.title} />
      </div>

      {/* Main Content Layout with Sticky Sidebar */}
      <div className="article-layout">
        
        {/* Sticky Sidebar */}
        <aside className="article-sidebar">
          <h3>
            <BookOpen size={16} /> Table of Contents
          </h3>
          <ul className="toc-list">
            {post.sections.map((sec, i) => (
              <li key={i}>
                <a href={`#section-${i}`}>
                  {sec.heading}
                </a>
              </li>
            ))}
            <li>
              <a href="#article-faqs">Frequently Asked Questions</a>
            </li>
          </ul>

          <div className="sidebar-admission-card">
            <Award size={24} color="#ff6b2b" style={{ margin: '0 auto 8px' }} />
            <h4>Admissions Open (2026-27)</h4>
            <p>Nursery to Class 10. Enroll at the best private school in Goraul.</p>
            <Link to="/admission-enquiry" className="btn-sidebar-enquire">
              Enquire Online
            </Link>
          </div>
        </aside>

        {/* Article Body Content */}
        <article className="article-body-content">
          
          {/* Intro Callout */}
          <div className="article-intro-callout">
            <p>{post.intro}</p>
          </div>

          {/* Dynamic Sections */}
          {post.sections.map((sec, i) => (
            <section key={i} id={`section-${i}`} className="content-section">
              <h2>{sec.heading}</h2>
              <p>{sec.content}</p>
            </section>
          ))}

          {/* FAQs Accordion */}
          <section id="article-faqs" className="article-faqs">
            <h2>
              <HelpCircle size={22} color="#0c278c" /> Frequently Asked Questions
            </h2>
            {post.faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <button 
                  className="faq-question" 
                  onClick={() => toggleFaq(i)}
                  aria-expanded={activeFaq === i}
                >
                  <span>{faq.q}</span>
                  {activeFaq === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {activeFaq === i && (
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* Keywords & Tags */}
          <div className="article-tags-row">
            <span className="tag-label">Target Keywords:</span>
            <span className="tag-pill">#{post.primaryKeyword.replace(/\s+/g, '')}</span>
            {post.secondaryKeywords.map((kw, i) => (
              <span key={i} className="tag-pill">#{kw.replace(/\s+/g, '')}</span>
            ))}
          </div>

          {/* Conversion CTA Box */}
          <section className="article-cta-box">
            <h3>Give Your Child the Advantage of the Best Private School in Goraul</h3>
            <p>
              Prabhat Tara Vidya Niketan is committed to nurturing excellence from Nursery to Class 10. 
              Schedule a campus tour or apply online today.
            </p>
            <div className="cta-actions">
              <Link to="/admission-enquiry" className="btn-apply">
                Apply for Admission Online
              </Link>
              <a href="tel:7654164726" className="btn-call">
                <Phone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} />
                Call +91 76541 64726
              </a>
            </div>
          </section>

        </article>
      </div>

      {/* Related Articles Section */}
      <section className="related-articles-section">
        <div className="related-header">
          <h2>Explore More Guides for Parents in Goraul & Vaishali</h2>
          <p>Read expert insights on schooling, admissions, exam readiness, and child care.</p>
        </div>
        <div className="related-grid">
          {relatedPosts.map(rel => (
            <article key={rel.id} className="blog-card" style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #edf2f7', display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: '170px', overflow: 'hidden' }}>
                <img src={rel.coverImage} alt={rel.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
              </div>
              <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <span style={{ fontSize: '0.75rem', color: '#0c278c', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.4rem' }}>{rel.category}</span>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.35, marginBottom: '0.6rem', color: '#011673' }}>
                  <Link to={`/blog/${rel.slug}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {rel.title}
                  </Link>
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#6c757d', lineHeight: 1.5, flexGrow: 1, marginBottom: '1rem' }}>
                  {rel.excerpt.slice(0, 110)}...
                </p>
                <Link to={`/blog/${rel.slug}`} style={{ color: '#ff6b2b', fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <span>Read Article</span>
                  <ArrowRight size={13} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

    </div>
  );
}
